/**
 * Меню одной таблицей — с деревом категорий слева.
 *
 * Дерево слева — фильтр, а не место. Таблица остаётся одной на всё
 * меню: цены правятся списком, поиск ищет по всему сразу, ходить
 * «внутрь» папок по-прежнему никуда не нужно (АРХ-283 жив). Дерево
 * лишь показывает строение — группы, а в них подгруппы с отступом
 * и счётчиком — и сужает таблицу одним касанием. Нажали «Бар» —
 * видна вся ветка вместе с подгруппами; нажали «Напитки» — только
 * они; нажали ещё раз — снова всё меню.
 *
 * Категория стала свойством позиции, а не местом, куда надо зайти.
 */
import { useState } from 'react';
import { branchIds, formatMoney, kindFromFlags, kindInfo, PRODUCT_KINDS, stationOf } from '@dastarhan/core';

export type Cat = {
  id: string;
  name: string;
  parentId: string | null;
  station: string | null;
  sort: number;
  isActive: boolean;
};

export type Row = {
  id: string;
  name: string;
  sku: string | null;
  categoryId: string | null;
  price: number;
  unit: string;
  minStock: number | null;
  isActive: boolean;
  trackStock: boolean;
  isIngredient: boolean;
  photoUrl: string | null;
  hasRecipe: boolean;
  modCount: number;
};

const UNIT_RU: Record<string, string> = {
  PIECE: 'Штука', KG: 'Килограмм', L: 'Литр', PORTION: 'Порция',
};

const STATION_RU: Record<string, string> = { kitchen: 'кухня', bar: 'бар' };

export function MenuTable({
  cats, rows, onAddCat, onEditCat, onDeleteCat,
  onOpen, onAdd, onArchive, onCleanEmpty, onMove,
}: {
  cats: Cat[];
  rows: Row[];
  /** новая категория; parentId — сразу подгруппой выбранной группы */
  onAddCat: (parentId: string | null) => void;
  onEditCat: (c: Cat) => void;
  onDeleteCat: (c: Cat) => void;
  onOpen: (r: Row) => void;
  onAdd: (categoryId: string | null) => void;
  onArchive: (r: Row) => void;
  onCleanEmpty: (list: Cat[]) => void;
  /** перенести отмеченные позиции в другую категорию */
  onMove: (ids: string[], categoryId: string) => void;
}) {
  const [pick, setPick] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [showOff, setShowOff] = useState(false);
  const [kindFilter, setKindFilter] = useState<string>('');
  const [marked, setMarked] = useState<Set<string>>(new Set());

  const norm = (s: string) => s.toLowerCase().trim();

  const live = cats.filter((c) => c.isActive);
  const byId = new Map(live.map((c) => [c.id, c]));

  /* Группы и их подгруппы — руками, а не buildCatTree: здесь важен
     тот же порядок, что видит человек, включая осиротевшие подгруппы */
  const bySort = (a: Cat, b: Cat) => a.sort - b.sort || a.name.localeCompare(b.name, 'ru');
  const tops = live
    .filter((c) => c.parentId === null || !byId.has(c.parentId))
    .sort(bySort);
  const kidsOf = (id: string) => live.filter((c) => c.parentId === id).sort(bySort);

  const countIn = (catId: string) =>
    rows.filter((r) => r.categoryId === catId && r.isActive).length;
  /** группа считает вместе с подгруппами — как на витрине: «Бар · 4» */
  const countBranch = (catId: string) =>
    branchIds(catId, live).reduce((s, id) => s + countIn(id), 0);

  /* Пустые папки копятся от пробных нажатий и мешают выбирать */
  const emptyCats = live.filter((c) => countBranch(c.id) === 0);

  const typeOf = (r: Row) => kindFromFlags({
    isIngredient: r.isIngredient,
    trackStock: r.trackStock,
    price: r.price,
    hasRecipe: r.hasRecipe,
  });

  /* Выбрана группа — таблица показывает всю ветку (branchIds),
     выбрана подгруппа — только её */
  const pickSet = pick ? new Set(branchIds(pick, live)) : null;

  const shown = rows.filter((r) => {
    if (!showOff && !r.isActive) return false;
    if (pickSet && !pickSet.has(r.categoryId ?? '')) return false;
    if (kindFilter && typeOf(r) !== kindFilter) return false;
    if (q && !norm(r.name).includes(norm(q)) && !norm(r.sku ?? '').includes(norm(q))) return false;
    return true;
  });

  const toggle = (id: string) => {
    setMarked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const pickedCat = pick ? byId.get(pick) ?? null : null;
  const pickedParent = pickedCat?.parentId ? byId.get(pickedCat.parentId) ?? null : null;
  const pickedStation = pickedCat ? stationOf(pickedCat, pickedParent) : null;
  const offCount = rows.filter((r) => !r.isActive).length;

  /** Строка дерева: своя для группы и для подгруппы (отступ и «└»). */
  const treeRow = (c: Cat, child: boolean) => {
    const on = pick === c.id;
    const parent = c.parentId ? byId.get(c.parentId) ?? null : null;
    const st = stationOf(c, parent);
    return (
      <button key={c.id}
        onClick={() => setPick(on ? null : c.id)}
        style={nodeStyle(on, child)}>
        {child && <span style={{ opacity: .45, marginRight: 6 }}>└</span>}
        <span style={{
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{c.name}</span>
        {st && (
          <span title={`цех: ${STATION_RU[st] ?? st}${c.station ? '' : ' (от группы)'}`}
            style={{ marginLeft: 6, color: 'var(--ok-strong, #2f7d4f)', opacity: c.station ? 1 : .45 }}>
            ⎙
          </span>
        )}
        <b style={{ marginLeft: 'auto', paddingLeft: 8, opacity: .55, fontWeight: 600 }}>
          {child ? countIn(c.id) : countBranch(c.id)}
        </b>
      </button>
    );
  };

  return (
    <section style={{ display: 'flex', gap: 18, alignItems: 'flex-start' }}>
      {/* ── Дерево категорий: группы, подгруппы с отступом, счётчики ── */}
      <aside style={{ width: 236, flex: 'none' }}>
        <div className="hint" style={{
          margin: '0 0 8px', fontSize: 12, letterSpacing: '.06em',
          textTransform: 'uppercase', opacity: .6,
        }}>Категории</div>

        <button onClick={() => setPick(null)} style={nodeStyle(pick === null, false)}>
          Всё меню
          <b style={{ marginLeft: 'auto', paddingLeft: 8, opacity: .55, fontWeight: 600 }}>
            {rows.filter((r) => r.isActive).length}
          </b>
        </button>

        {tops.map((top) => (
          <div key={top.id}>
            {treeRow(top, false)}
            {kidsOf(top.id).map((k) => treeRow(k, true))}
          </div>
        ))}

        <button onClick={() => onAddCat(null)}
          style={{ ...nodeStyle(false, false), borderStyle: 'dashed', justifyContent: 'center' }}>
          ＋ Категория
        </button>
        {/* Подгруппа заводится из выбранной группы: родитель уже подставлен */}
        {pickedCat && !pickedCat.parentId && (
          <button onClick={() => onAddCat(pickedCat.id)}
            style={{ ...nodeStyle(false, true), borderStyle: 'dashed', justifyContent: 'center' }}>
            ＋ Подгруппа в «{pickedCat.name}»
          </button>
        )}

        {emptyCats.length > 1 && (
          <button
            title={`Пустые: ${emptyCats.map((c) => c.name).join(', ')}`}
            onClick={() => onCleanEmpty(emptyCats)}
            style={{ ...nodeStyle(false, false), opacity: .65, justifyContent: 'center' }}>
            Убрать пустые ({emptyCats.length})
          </button>
        )}
      </aside>

      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Действия над выбранной категорией — рядом с ней, а не в меню */}
        {pickedCat && (
          <div style={{
            display: 'flex', gap: 8, alignItems: 'center',
            marginBottom: 12, fontSize: 14, flexWrap: 'wrap',
          }}>
            <b>
              {pickedParent ? `${pickedParent.name} → ` : ''}{pickedCat.name}
            </b>
            {pickedStation && (
              <span style={{ opacity: .7 }}>
                цех: {STATION_RU[pickedStation] ?? pickedStation}
                {!pickedCat.station && ' (от группы)'}
              </span>
            )}
            <button className="btn small" onClick={() => onEditCat(pickedCat)}>
              Настроить
            </button>
            <button className="btn small ghost" onClick={() => onDeleteCat(pickedCat)}>
              Убрать категорию
            </button>
          </div>
        )}

        <div style={{
          display: 'flex', gap: 12, alignItems: 'center',
          flexWrap: 'wrap', marginBottom: 14,
        }}>
          <input className="field" placeholder="Найти позицию — название или артикул"
            value={q} onChange={(e) => setQ(e.target.value)}
            style={{ flex: 1, minWidth: 220, minHeight: 44 }} />

          <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}
            style={{ minHeight: 44 }}>
            <option value="">Все типы</option>
            {PRODUCT_KINDS.map((k) => (
              <option key={k.key} value={k.key}>{k.label}</option>
            ))}
          </select>

          {offCount > 0 && (
            <label className="check" style={{ fontSize: 13 }}>
              <input type="checkbox" checked={showOff}
                onChange={(e) => setShowOff(e.target.checked)} />
              Показать убранные ({offCount})
            </label>
          )}

          <button className="btn primary" onClick={() => onAdd(pick)}>
            ＋ Позиция
          </button>
        </div>

        {/* Отмеченные позиции переносятся пачкой: раскидать сорок блюд
            по категориям поодиночке — сорок открытий карточки */}
        {marked.size > 0 && (
          <div style={{
            display: 'flex', gap: 10, alignItems: 'center',
            padding: '10px 14px', marginBottom: 12,
            background: 'var(--surface-sunk, #f5f6f8)', borderRadius: 10,
          }}>
            <b>Отмечено: {marked.size}</b>
            <select defaultValue="" style={{ minHeight: 40 }}
              onChange={(e) => {
                if (!e.target.value) return;
                onMove([...marked], e.target.value);
                setMarked(new Set());
                e.target.value = '';
              }}>
              <option value="">Перенести в категорию…</option>
              {tops.map((top) => [
                <option key={top.id} value={top.id}>{top.name}</option>,
                ...kidsOf(top.id).map((k) => (
                  <option key={k.id} value={k.id}>{'\u00A0\u00A0└ '}{k.name}</option>
                )),
              ])}
            </select>
            <button className="btn small ghost" onClick={() => setMarked(new Set())}>
              Снять отметки
            </button>
          </div>
        )}

        {shown.length === 0 ? (
          <p className="note">
            {q || kindFilter || pick
              ? 'Ничего не нашлось. Сбросьте фильтры или поиск.'
              : 'Меню пустое. Создайте первую позицию кнопкой выше.'}
          </p>
        ) : (
          <table className="grid" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ width: 34 }} />
                <th>Наименование</th>
                <th>Категория</th>
                <th>Тип</th>
                <th>Ед. изм.</th>
                <th className="num">Цена</th>
                <th className="num">Мин.</th>
                <th>Калькуляция</th>
                <th>Модиф.</th>
                <th style={{ width: 44 }} />
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => {
                const info = kindInfo(typeOf(r));
                const cat = r.categoryId ? byId.get(r.categoryId) ?? cats.find((c) => c.id === r.categoryId) : undefined;
                return (
                  <tr key={r.id} style={{ opacity: r.isActive ? 1 : .5 }}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={marked.has(r.id)}
                        onChange={() => toggle(r.id)} />
                    </td>

                    <td onClick={() => onOpen(r)} style={{ cursor: 'pointer' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {r.photoUrl
                          ? <img src={r.photoUrl} alt=""
                              style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'cover' }} />
                          : <span style={{
                              width: 32, height: 32, borderRadius: 6, flex: 'none',
                              display: 'grid', placeItems: 'center', fontSize: 14,
                              background: 'var(--surface-sunk, #f0f1f3)',
                            }}>🍽</span>}
                        <div style={{ minWidth: 0 }}>
                          <b>{r.name}</b>
                          {!r.isActive && <span style={{ marginLeft: 6, fontSize: 11, opacity: .7 }}>в архиве</span>}
                          <div style={{ fontSize: 11, opacity: .55 }}>{r.sku ?? '—'}</div>
                        </div>
                      </div>
                    </td>

                    {/* Категорию видно в строке: при поиске по всему меню
                        иначе непонятно, где лежит найденное */}
                    <td>
                      {cat
                        ? <button className="btn small ghost"
                            onClick={() => setPick(cat.id)}>{cat.name}</button>
                        : <span style={{ opacity: .5 }}>без категории</span>}
                    </td>

                    <td onClick={() => onOpen(r)} style={{ cursor: 'pointer' }}>
                      <span style={{
                        padding: '2px 10px', borderRadius: 999, fontSize: 13,
                        background: 'var(--ok-quiet, #e8f3ec)',
                        color: 'var(--ok-strong, #2f7d4f)',
                      }}>{info.label}</span>
                    </td>

                    <td>{UNIT_RU[r.unit] ?? r.unit}</td>
                    <td className="num">{info.inMenu ? formatMoney(r.price) : '—'}</td>
                    <td className="num">{r.minStock ?? 0}</td>

                    <td>
                      {info.hasRecipe
                        ? (r.hasRecipe
                            ? <span style={{ color: 'var(--ok-strong, #2f7d4f)', fontWeight: 600 }}>Есть</span>
                            /* Блюдо без техкарты не списывает сырьё
                               и показывает нулевую себестоимость */
                            : <span title="Себестоимость не считается, сырьё не списывается"
                                style={{
                                  color: 'var(--warn, #b8760f)', fontWeight: 600,
                                  background: 'var(--warn-quiet, #fdf3e3)',
                                  padding: '1px 8px', borderRadius: 999,
                                }}>Нет</span>)
                        : <span style={{ opacity: .5 }}>не нужна</span>}
                    </td>

                    <td>{r.modCount > 0 ? r.modCount : <span style={{ opacity: .5 }}>—</span>}</td>

                    <td>
                      <button className="btn small ghost"
                        title={r.isActive ? 'Убрать из меню, история цела' : 'Вернуть в меню'}
                        onClick={() => onArchive(r)}>
                        {r.isActive ? '🗑' : '↩'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <p className="hint" style={{ marginTop: 12 }}>
          Показано {shown.length} из {rows.filter((r) => r.isActive).length}.
          Категория слева — фильтр: нажмите её ещё раз, чтобы снова видеть всё меню.
        </p>
      </div>
    </section>
  );
}

/** Вид строки дерева. Стили в разметке: чужой файл их не перебьёт. */
function nodeStyle(on: boolean, child: boolean): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minHeight: 40,
    padding: child ? '0 10px 0 26px' : '0 10px 0 12px',
    marginBottom: 4,
    borderRadius: 10,
    border: `1px solid ${on ? 'var(--accent, #c46a2f)' : 'var(--line, #e0e2e5)'}`,
    background: on ? 'var(--accent, #c46a2f)' : 'var(--surface, #fff)',
    color: on ? '#fff' : 'var(--ink, #23262b)',
    font: 'inherit',
    fontSize: 14,
    textAlign: 'left',
    cursor: 'pointer',
  };
}
